// owned by WP-TRUST · pieces shared by the Trust Centre, the rollout and the audit log (APPLE-SPEC §6.2, §7, §7.1): the page header (38 px
// app-icon tile, Title 1 with the other language's name, the verdict and the meta line hung on the title's text edge, the action group at
// the end), status as text (a 6 px dot or a glyph + a coloured word, never a tinted chip), the meta line, definition lists, the System
// Settings pieces of the admin pages (source list, grouped form rows, pop-up buttons), honest load states, durations in words through Intl.
// StatusItem / StatusLine / MetaLine / SourceList / FormGroup are local twins of WP-THEME's APPLE-SPEC §7.1 primitives (same anatomy and
// values) until those ship in @ncp/ui; swap the imports then.
import { useState, type ComponentProps, type ReactNode, type SelectHTMLAttributes } from "react";
import { Link, NavLink } from "react-router";
import { AlertTriangle, Check, ChevronRight, ChevronsUpDown, Cpu, Hourglass, Lock, Upload, type LucideIcon } from "lucide-react";
import { useLocale, useTranslation } from "@ncp/i18n";
import { Card, Notice, SkeletonBlock } from "../theme/primitives";
import { formatDay, formatTime, dubaiDateKey } from "../lib/time";
import { Drawer } from "../admin/Drawer";
import { problemOf, type Bi, type SourceState } from "./api";
import "./trust.css";

export type Lang = "en" | "ar";

export function useLang(): Lang {
  const { locale } = useLocale();
  return locale === "ar" ? "ar" : "en";
}

/** The current language's side of a bilingual value. */
export function bi(v: Bi | null | undefined, lang: Lang): string {
  if (!v) return "";
  return lang === "ar" ? v.ar || v.en : v.en || v.ar;
}

const TASHKEEL = /[ً-ْٰ]/g;

/** "30 minutes" / «30 دقيقة», "10 hours" / «10 ساعات» (Intl plural forms, Western digits, no diacritics). */
export function duration(value: number, unit: "second" | "minute" | "hour" | "day", lang: Lang): string {
  const f = new Intl.NumberFormat(lang === "ar" ? "ar-AE-u-nu-latn" : "en-GB", { style: "unit", unit, unitDisplay: "long", maximumFractionDigits: 1 });
  return f.format(value).replace(TASHKEEL, "").replace(/[‎‏؜]/g, "");
}

const EN_DAY = new Intl.DateTimeFormat("en-US", { day: "numeric", month: "short", timeZone: "Asia/Dubai" });

/** "29 Sep" (APPLE-SPEC: never "29 Sept", which en-GB short months now give) / «29 سبتمبر» (Dubai time). */
export function day(at: string | number | Date, lang: Lang): string {
  if (lang === "ar") return formatDay(at, lang);
  const d = at instanceof Date ? at : new Date(at);
  if (Number.isNaN(d.getTime())) return "";
  const p = EN_DAY.formatToParts(d);
  return `${p.find((x) => x.type === "day")?.value ?? ""} ${p.find((x) => x.type === "month")?.value ?? ""}`;
}

/** "14:26" today, "12 Oct 14:26" another day (Dubai time; «12 أكتوبر 14:26» in Arabic). */
export function when(at: string | null | undefined, lang: Lang): string {
  if (!at) return "";
  return dubaiDateKey(at) === dubaiDateKey(Date.now()) ? formatTime(at, lang) : `${day(at, lang)} ${formatTime(at, lang)}`;
}

export function num(value: number | null | undefined, lang: Lang, digits = 0): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return new Intl.NumberFormat(lang === "ar" ? "ar-AE-u-nu-latn" : "en-GB", { maximumFractionDigits: digits }).format(value);
}

// ------------------------------------------------------------------ page header (§6.2)

export function TrustScreenHeader({ icon: Icon, titleKey, verdict, meta, actions }: {
  icon: LucideIcon;
  titleKey: string;
  verdict?: ReactNode;
  /** The meta line under the verdict (a MetaLine or a StatusLine). */
  meta?: ReactNode;
  /** The action group at the end: the default action last. */
  actions?: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <header className="ec-tr-head" data-econic-page-header="">
      <div className="top">
        <span className="ec-tr-tile" aria-hidden><Icon /></span>
        <div className="ttl">
          <h1 tabIndex={-1} data-econic-h1="">{t(titleKey)}</h1>
        </div>
        {actions ? <div className="acts">{actions}</div> : null}
      </div>
      {verdict ? <p className="verdict">{verdict}</p> : null}
      {meta ? <div className="meta">{meta}</div> : null}
    </header>
  );
}

// ------------------------------------------------------------------ one list per section, the details in a side sheet (simpler pass)

export interface Fact {
  key: string;
  /** A muted inline glyph (the overview's six statements). */
  icon?: LucideIcon;
  label: ReactNode;
  value?: ReactNode;
  /** A StatusItem. */
  status?: ReactNode;
  /** Opens another screen. */
  to?: string;
  /** Opens the side sheet with these details. */
  detail?: ReactNode;
  detailTitle?: ReactNode;
  /** The sheet's footer (the consequence at the start, the action last). */
  detailFooter?: ReactNode;
}

/** A grouped list (iOS Settings): a row per fact, label · value · status · chevron; a row with details opens them in a side sheet. */
export function FactList({ title, count, rows, footer, actions, variant, className }: {
  title?: ReactNode;
  count?: ReactNode;
  rows: Array<Fact | null | false | undefined>;
  footer?: ReactNode;
  actions?: ReactNode;
  variant?: "summary";
  className?: string;
}) {
  const [open, setOpen] = useState<string | null>(null);
  const list = rows.filter(Boolean) as Fact[];
  const current = list.find((r) => r.key === open);
  return (
    <section className={`ec-card ec-tr-facts ${variant ?? ""} ${className ?? ""}`}>
      {title || actions ? (
        <header className="ec-tr-facts-h">
          {title ? <h2>{title}{count !== undefined ? <span className="n"> {count}</span> : null}</h2> : <span className="flex-1" />}
          {actions}
        </header>
      ) : null}
      <ul role="list">
        {list.map((r) => {
          const inner = (
            <>
              {r.icon ? <r.icon className="icn" aria-hidden /> : null}
              <span className="lb">{r.label}</span>
              <span className="vl">{r.value}</span>
              <span className="st">{r.status}</span>
              {r.to || r.detail ? <ChevronRight className="ch" aria-hidden /> : <span className="ch" aria-hidden />}
            </>
          );
          const cls = `row${r.icon ? " ic" : ""}`;
          return (
            <li key={r.key} data-fact={r.key}>
              {r.to ? <Link to={r.to} className={cls}>{inner}</Link>
                : r.detail ? <button type="button" className={cls} aria-haspopup="dialog" onClick={() => setOpen(r.key)}>{inner}</button>
                  : <div className={cls}>{inner}</div>}
            </li>
          );
        })}
      </ul>
      {footer ? <footer className="ec-tr-facts-f">{footer}</footer> : null}
      {current?.detail ? (
        <Sheet title={current.detailTitle ?? current.label} footer={current.detailFooter} onClose={() => setOpen(null)} id={current.key}>
          {current.detail}
        </Sheet>
      ) : null}
    </section>
  );
}

/** The side sheet of a fact: the Trust Centre's own type inside the shared drawer. */
export function Sheet({ id, title, footer, onClose, children }: { id: string; title: ReactNode; footer?: ReactNode; onClose: () => void; children: ReactNode }) {
  return (
    <Drawer labelId={`ec-tr-sheet-${id}`} title={title} footer={footer} onClose={onClose}>
      <div className="ec-tr-sheet">{children}</div>
    </Drawer>
  );
}

// ------------------------------------------------------------------ card with a lead line

/** A card whose explanatory sentence sits under the header, in the body (the header keeps the title, a count and actions only). */
export function TCard({ lead, children, body = "default", ...rest }: ComponentProps<typeof Card> & { lead?: ReactNode }) {
  return (
    <Card body={body} {...rest}>
      {lead ? <p className={`ec-tr-lead ${body}`}>{lead}</p> : null}
      {children}
    </Card>
  );
}

/** A plain count in a card header (13 / 600 label-2; red only when something is due). */
export function Count({ n, due, title }: { n: ReactNode; due?: boolean; title?: string }) {
  return <span className={`ec-tr-countb ${due ? "due" : ""}`} title={title}>{n}</span>;
}

// ------------------------------------------------------------------ definition list

export function Dl({ rows }: { rows: Array<[ReactNode, ReactNode] | null | false | undefined> }) {
  return (
    <dl className="ec-tr-dl">
      {rows.filter(Boolean).map((r, i) => {
        const [k, v] = r as [ReactNode, ReactNode];
        return (
          <div key={i} style={{ display: "contents" }}>
            <dt>{k}</dt>
            <dd>{v}</dd>
          </div>
        );
      })}
    </dl>
  );
}

export function Mono({ children }: { children: ReactNode }) {
  return <bdi className="ec-tr-mono" dir="ltr">{children}</bdi>;
}

// ------------------------------------------------------------------ status is text (§7.1 StatusLine / StatusItem, §7 10.10)

export type Tone = "good" | "warn" | "serious" | "crit" | "info" | "neutral";
export type StatusKind = Tone | "plain" | "live";

/**
 * One status: a 6 px dot (or a glyph) and a coloured word, inline text so a figure keeps its space. good = ✓ green text; warn = amber dot;
 * serious = orange dot; info = navy dot with label text; neutral = grey dot with secondary text; crit = red-ink 600 with its glyph and no
 * dot (only when a line was crossed or something failed); plain = the word alone; live = the teal dot of the live badge.
 * `icon` replaces the dot with a glyph (⌛ awaiting, 🔒 blocked); `icon={null}` keeps the dot even where a glyph is the default.
 */
export function StatusItem({ kind, icon, children, title }: { kind: StatusKind; icon?: LucideIcon | null; children: ReactNode; title?: string }) {
  const Glyph = icon === undefined ? (kind === "good" ? Check : kind === "crit" ? AlertTriangle : null) : icon;
  return (
    <span className={`ec-tr-st ${kind} ${Glyph ? "g" : ""}`} title={title} data-tone={kind}>
      {Glyph ? <Glyph aria-hidden strokeWidth={2} /> : null}
      {children}
    </span>
  );
}

/** A row of status items, 14 px apart (12 / 16; Arabic 13 / 19). */
export function StatusLine({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={`ec-tr-stline ${className ?? ""}`}>{children}</span>;
}

/** The former badge name: every state in the Trust Centre is a StatusItem now. */
export function StateBadge({ tone, icon, children, title }: { tone: Tone; icon?: LucideIcon | null; children: ReactNode; title?: string }) {
  return <StatusItem kind={tone} icon={icon} title={title}>{children}</StatusItem>;
}

/** The meta line (12 px label-2 with 0.5 × 12 px rules between the items). */
export function MetaLine({ children }: { children: ReactNode }) {
  return <div className="ec-tr-metaline">{children}</div>;
}
export function MetaSep() {
  return <span className="ec-tr-sepv" aria-hidden />;
}

const SOURCE_KIND: Record<SourceState, { kind: StatusKind; icon?: LucideIcon | null }> = {
  live: { kind: "live" },
  updated: { kind: "neutral" },
  due: { kind: "warn" },
  late: { kind: "serious" },
  stale: { kind: "serious" },
  degraded: { kind: "serious" },
  blocked: { kind: "neutral", icon: Lock },
  "upload-only": { kind: "neutral", icon: Upload },
  awaiting: { kind: "neutral", icon: Hourglass },
  model: { kind: "neutral", icon: Cpu },
};

/** A source's state as page-kit's text live badge draws it (live-sources §10.3): "Live" only when the state is live; the word always shows. */
export function SourceBadge({ state, at }: { state: SourceState; at?: string | null }) {
  const { t } = useTranslation();
  const lang = useLang();
  const s = SOURCE_KIND[state] ?? { kind: "neutral" as StatusKind };
  const word = t(`assistant:econic.trust.sources.states.${state}`, { defaultValue: state });
  return (
    <span data-source-state={state}>
      <StatusItem kind={s.kind} icon={s.icon ?? null}>{word}{state === "live" && at ? ` ${formatTime(at, lang)}` : ""}</StatusItem>
    </span>
  );
}

// ------------------------------------------------------------------ System Settings pieces (admin pages; §7.1, §8 "Admin · Accounts")

export interface SourceListItem { to: string; icon: LucideIcon; label: string; count?: number | null; due?: boolean; end?: boolean }

/** The Finder / System Settings sidebar: 12 / 600 group heads over 28 px rows (navy glyph, label, count at the end). */
export function SourceList({ label, groups, footer }: { label: string; groups: Array<{ head: string; items: SourceListItem[] }>; footer?: ReactNode }) {
  return (
    <nav className="ec-card ec-tr-srclist" aria-label={label}>
      {groups.filter((g) => g.items.length).map((g) => (
        <div key={g.head} className="grp" role="group" aria-label={g.head}>
          <div className="sl-h">{g.head}</div>
          {g.items.map((it) => (
            <NavLink key={it.to} to={it.to} end={it.end ?? true} className={({ isActive }) => (isActive ? "on" : undefined)}>
              <it.icon aria-hidden strokeWidth={1.5} />
              <span className="lb">{it.label}</span>
              {typeof it.count === "number" ? <span className={`n ${it.due ? "due" : ""}`}>{it.count}</span> : null}
            </NavLink>
          ))}
        </div>
      ))}
      {footer}
    </nav>
  );
}

/** A grouped form (an 8 px --card-2 group with 38 px rows and inset hairlines), with an optional 12 / 600 section head. */
export function FormGroup({ head, children }: { head?: ReactNode; children: ReactNode }) {
  return (
    <div className="ec-tr-fgrp-w">
      {head ? <div className="ec-tr-fsec">{head}</div> : null}
      <div className="ec-tr-fgrp">{children}</div>
    </div>
  );
}

/** One row of a grouped form: the label at the start, the value or control at the end. */
export function FormRow({ label, htmlFor, children }: { label: ReactNode; htmlFor?: string; children: ReactNode }) {
  return (
    <div className="ec-tr-fr">
      {htmlFor ? <label className="fl" htmlFor={htmlFor}>{label}</label> : <span className="fl">{label}</span>}
      <span className="fv">{children}</span>
    </div>
  );
}

/** The 24 px pop-up button (a native select: white, 0.5 px edge, the control shadow, ⌃⌄ at the end). */
export function PopUp({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <span className={`ec-tr-popup ${className ?? ""}`}>
      <select {...rest}>{children}</select>
      <ChevronsUpDown aria-hidden strokeWidth={1.75} />
    </span>
  );
}

// ------------------------------------------------------------------ honest states

export function LoadState({ error, onRetry, rows = 4 }: { error?: unknown; onRetry?: () => void; rows?: number }) {
  const { t } = useTranslation();
  if (!error) {
    return (
      <div className="flex flex-col gap-2 p-3" aria-busy="true" aria-label={t("assistant:econic.trust.state.loading")}>
        {Array.from({ length: rows }, (_, i) => <SkeletonBlock key={i} height={14} width={`${92 - i * 9}%`} />)}
      </div>
    );
  }
  const p = problemOf(error);
  if (p === "forbidden") return <Notice kind="no-access" className="m-3">{t("assistant:econic.trust.state.forbidden")}</Notice>;
  if (p === "unavailable") return <Notice kind="dependency" className="m-3" title={t("assistant:econic.trust.state.unavailable")} />;
  return (
    <Notice
      kind="error"
      className="m-3"
      title={t("assistant:econic.trust.state.failed")}
      action={onRetry ? <button type="button" className="ec-btn secondary sm" onClick={onRetry}>{t("assistant:econic.trust.state.retry")}</button> : undefined}
    >
      {t("assistant:econic.trust.state.failedHint")}
    </Notice>
  );
}

/** A section a contributor did not answer: named, nothing filled in. */
export function MissingSection({ name }: { name: string }) {
  const { t } = useTranslation();
  return <Notice kind="warning" className="m-3">{t("assistant:econic.trust.state.sectionMissing", { section: name })}</Notice>;
}
